import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Shield, RefreshCw, LogOut, Loader2, AlertCircle, CheckCircle2, Clock, X,
  Search, Receipt, DollarSign, Copy, Send,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ADMIN_KEY_LS = 'nofee_admin_key';
const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const STATE_META = {
  pending:  { label: 'Pending review', pill: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  approved: { label: 'Approved',        pill: 'bg-sky-500/15 text-sky-300 border-sky-500/40' },
  paid:     { label: 'Paid',            pill: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  rejected: { label: 'Rejected',        pill: 'bg-rose-500/15 text-rose-300 border-rose-500/40' },
};

const FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'pending',  label: 'Pending' },
  { key: 'approved', label: 'Approved' },
  { key: 'paid',     label: 'Paid' },
  { key: 'rejected', label: 'Rejected' },
];

const ReimbursementsAdmin = () => {
  const [adminKey, setAdminKey] = useState(() => { try { return localStorage.getItem(ADMIN_KEY_LS) || ''; } catch { return ''; } });
  const [authed, setAuthed] = useState(!!adminKey);
  const [keyInput, setKeyInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState({ rows: [], summary: {} });
  const [filter, setFilter] = useState('pending');
  const [query, setQuery] = useState('');
  const [actingId, setActingId] = useState('');
  const [payModal, setPayModal] = useState(null);   // {id, name, amount}
  const [payRef, setPayRef] = useState('');

  const fetchData = async (key, status = filter) => {
    setLoading(true); setError('');
    try {
      const r = await axios.get(`${API}/admin/reimbursements`, {
        headers: { 'X-Admin-Key': key },
        params: { status },
      });
      setData(r.data);
      setAuthed(true);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 403) {
        setError('Invalid admin key.');
        setAuthed(false);
        try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
      } else {
        setError(err?.response?.data?.detail || 'Could not load reimbursements.');
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { if (adminKey) fetchData(adminKey, filter); /* eslint-disable-next-line */ }, [filter]);

  const onSubmitKey = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    try { localStorage.setItem(ADMIN_KEY_LS, keyInput.trim()); } catch {}
    setAdminKey(keyInput.trim());
    fetchData(keyInput.trim(), filter);
  };

  const signOut = () => {
    try { localStorage.removeItem(ADMIN_KEY_LS); } catch {}
    setAdminKey(''); setKeyInput(''); setAuthed(false); setData({ rows: [], summary: {} });
  };

  const onApprove = async (id) => {
    setActingId(id);
    try {
      await axios.post(`${API}/admin/reimbursements/${id}/approve`, {}, { headers: { 'X-Admin-Key': adminKey } });
      await fetchData(adminKey, filter);
    } catch (err) { setError(err?.response?.data?.detail || 'Approve failed.'); } finally { setActingId(''); }
  };

  const onReject = async (id) => {
    const reason = window.prompt('Reject reason (optional):');
    if (reason === null) return;
    setActingId(id);
    try {
      await axios.post(`${API}/admin/reimbursements/${id}/reject`, { reason }, { headers: { 'X-Admin-Key': adminKey } });
      await fetchData(adminKey, filter);
    } catch (err) { setError(err?.response?.data?.detail || 'Reject failed.'); } finally { setActingId(''); }
  };

  const openPayModal = (r) => { setPayModal(r); setPayRef(''); };

  const onMarkPaid = async () => {
    if (!payModal) return;
    setActingId(payModal.id);
    try {
      await axios.post(`${API}/admin/reimbursements/${payModal.id}/mark-paid`,
        { payment_reference: payRef.trim(), admin_notes: '' },
        { headers: { 'X-Admin-Key': adminKey } });
      setPayModal(null);
      await fetchData(adminKey, filter);
    } catch (err) { setError(err?.response?.data?.detail || 'Mark paid failed.'); } finally { setActingId(''); }
  };

  const onCopy = async (text) => { try { await navigator.clipboard.writeText(text); } catch {} };

  const rows = useMemo(() => {
    let all = data.rows || [];
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      all = all.filter((r) =>
        (r.restaurant_name || '').toLowerCase().includes(q) ||
        (r.customer_email || '').toLowerCase().includes(q) ||
        (r.customer_name || '').toLowerCase().includes(q) ||
        (r.gift_id || '').toLowerCase().includes(q),
      );
    }
    return all;
  }, [data, query]);

  if (!authed) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-4" data-testid="reimbursements-admin-gate">
        <form onSubmit={onSubmitKey} className="w-full max-w-md rounded-2xl border border-yellow-500/30 bg-zinc-950 p-7">
          <div className="w-10 h-10 mb-4 rounded-xl bg-yellow-500/[0.10] border border-yellow-500/40 flex items-center justify-center"><Shield className="w-5 h-5 text-yellow-300" /></div>
          <h1 className="text-xl font-extrabold tracking-tight mb-1">Admin sign-in</h1>
          <p className="text-[13px] text-white/55 mb-5">Enter the NoFee admin key to manage reimbursements.</p>
          <input type="password" value={keyInput} onChange={(e) => setKeyInput(e.target.value)} placeholder="X-Admin-Key" autoFocus
            data-testid="reimbursements-admin-key-input"
            className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/12 focus:border-yellow-500/60 outline-none text-[14px] mb-3" />
          {error && <p className="mb-3 text-[12.5px] text-rose-300">{error}</p>}
          <button type="submit" data-testid="reimbursements-admin-key-submit" className="w-full px-4 py-3 rounded-xl bg-gradient-to-b from-yellow-300 to-yellow-500 text-black font-bold text-[14px]">Sign in</button>
        </form>
      </div>
    );
  }

  const s = data.summary || {};

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white antialiased" data-testid="reimbursements-admin-page">
      <div className="sticky top-0 z-20 bg-[#0A0A0A]/95 backdrop-blur border-b border-white/[0.06]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-yellow-500/[0.10] border border-yellow-500/40 flex items-center justify-center"><Receipt className="w-4 h-4 text-yellow-300" /></div>
            <div>
              <p className="text-[10.5px] uppercase tracking-[0.22em] text-yellow-300 font-bold">Admin</p>
              <h1 className="text-[15px] font-bold leading-none mt-0.5">Gift reimbursements</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => fetchData(adminKey, filter)} disabled={loading} data-testid="reimbursements-refresh"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/12 hover:border-white/25 text-white/80 hover:text-white text-[12.5px] font-semibold">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh
            </button>
            <button onClick={signOut} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/12 hover:border-rose-500/40 text-white/55 hover:text-rose-300 text-[12.5px] font-semibold"><LogOut className="w-3.5 h-3.5" /> Sign out</button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-7 pb-20">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="reimbursements-summary">
          {[
            { key: 'pending',  label: 'Pending review', amt: s.pending_cents || 0,  cnt: s.pending_count || 0,  tone: 'border-amber-500/30 bg-amber-500/[0.05]' },
            { key: 'approved', label: 'Approved',       amt: s.approved_cents || 0, cnt: s.approved_count || 0, tone: 'border-sky-500/30 bg-sky-500/[0.05]' },
            { key: 'paid',     label: 'Paid',           amt: s.paid_cents || 0,     cnt: s.paid_count || 0,     tone: 'border-emerald-500/30 bg-emerald-500/[0.05]' },
            { key: 'rejected', label: 'Rejected',       amt: s.rejected_cents || 0, cnt: s.rejected_count || 0, tone: 'border-rose-500/30 bg-rose-500/[0.05]' },
          ].map(({ key, label, amt, cnt, tone }) => (
            <div key={key} className={`p-4 rounded-2xl border ${tone}`} data-testid={`reimbursements-tile-${key}`}>
              <div className="flex items-center gap-2"><DollarSign className="w-3.5 h-3.5 text-white/55" /><span className="text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold">{label}</span></div>
              <div className="mt-2 text-2xl font-extrabold tabular-nums">{fmt$(amt)}</div>
              <div className="text-[10.5px] text-white/45 mt-0.5">{cnt} {cnt === 1 ? 'item' : 'items'}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-2" data-testid="reimbursements-filters">
            {FILTERS.map((f) => (
              <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`reimbursements-filter-${f.key}`}
                className={`px-3.5 py-1.5 rounded-full border text-[12px] font-bold transition-all ${filter === f.key ? 'bg-yellow-500/[0.10] border-yellow-500/45 text-yellow-200' : 'border-white/10 hover:border-white/25 text-white/55 hover:text-white/80'}`}>{f.label}</button>
            ))}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-3.5 h-3.5 text-white/35 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search restaurant, customer, gift…"
              data-testid="reimbursements-search"
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-black/40 border border-white/12 focus:border-yellow-500/50 outline-none text-[12.5px]" />
          </div>
        </div>

        {error && <div className="mt-4 p-4 rounded-2xl border border-rose-500/30 bg-rose-500/[0.06] text-rose-200 text-[13.5px]">{error}</div>}

        <div className="mt-5 rounded-2xl border border-white/[0.07] bg-white/[0.015] overflow-hidden">
          {rows.length === 0 && !loading ? (
            <div className="p-10 text-center text-white/45 text-[14px]">No reimbursements match this filter.</div>
          ) : (
            <ul className="divide-y divide-white/[0.05]">
              {rows.map((r) => {
                const meta = STATE_META[r.status] || STATE_META.pending;
                const canApprove = r.status === 'pending';
                const canPay = r.status === 'pending' || r.status === 'approved';
                const canReject = r.status !== 'paid' && r.status !== 'rejected';
                return (
                  <li key={r.id} className="p-4 sm:p-5 flex flex-col gap-3" data-testid={`reimbursements-row-${r.id}`}>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[15px] font-bold text-white">{fmt$(r.amount_cents)}</span>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-md border text-[10px] font-bold uppercase tracking-wider ${meta.pill}`}>{meta.label}</span>
                        </div>
                        <div className="mt-1 text-[12px] text-white/65 truncate">
                          <strong className="text-white">{r.restaurant_name || '—'}</strong>
                          <span className="text-white/40 mx-1.5">•</span>
                          <span>{r.customer_name || r.customer_email || '—'}</span>
                        </div>
                        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-white/45">
                          <span className="font-mono">{r.gift_id}</span>
                          <button onClick={() => onCopy(r.gift_id)} className="hover:text-yellow-300 inline-flex items-center gap-1"><Copy className="w-3 h-3" /></button>
                          <span>{new Date(r.created_at).toLocaleString()}</span>
                          {r.payment_reference && <span className="text-emerald-300">Ref: {r.payment_reference}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {canApprove && (
                          <button onClick={() => onApprove(r.id)} disabled={actingId === r.id} data-testid={`approve-${r.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-sky-500/30 hover:border-sky-500/55 bg-sky-500/[0.06] hover:bg-sky-500/[0.10] text-sky-200 text-[11.5px] font-bold disabled:opacity-50">
                            {actingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Clock className="w-3.5 h-3.5" />} Approve
                          </button>
                        )}
                        {canPay && (
                          <button onClick={() => openPayModal(r)} disabled={actingId === r.id} data-testid={`mark-paid-${r.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-500/30 hover:border-emerald-500/55 bg-emerald-500/[0.06] hover:bg-emerald-500/[0.10] text-emerald-200 text-[11.5px] font-bold disabled:opacity-50">
                            <Send className="w-3.5 h-3.5" /> Mark as paid
                          </button>
                        )}
                        {canReject && (
                          <button onClick={() => onReject(r.id)} disabled={actingId === r.id} data-testid={`reject-${r.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-rose-500/30 hover:border-rose-500/55 bg-rose-500/[0.06] hover:bg-rose-500/[0.10] text-rose-200 text-[11.5px] font-bold disabled:opacity-50">
                            <X className="w-3.5 h-3.5" /> Reject
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Pay modal */}
      {payModal && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setPayModal(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl border border-emerald-500/30 bg-zinc-950 p-6" data-testid="pay-modal">
            <h3 className="text-[18px] font-extrabold tracking-tight">Mark as paid</h3>
            <p className="mt-2 text-[12.5px] text-white/55">
              Send <strong className="text-yellow-300">{fmt$(payModal.amount_cents)}</strong> to <strong className="text-white">{payModal.restaurant_name}</strong> via Zelle, then record it here.
            </p>
            <label className="block mt-5">
              <span className="block text-[10.5px] uppercase tracking-[0.18em] text-white/55 font-bold mb-1.5">Payment reference (optional)</span>
              <input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="Zelle confirmation #" autoFocus
                data-testid="pay-modal-ref"
                className="w-full px-4 py-3 rounded-xl bg-black/40 border border-white/12 focus:border-emerald-500/50 outline-none text-[14px]" />
            </label>
            <div className="mt-5 flex gap-2">
              <button onClick={onMarkPaid} disabled={actingId === payModal.id} data-testid="pay-modal-confirm"
                className="flex-1 py-3 rounded-xl bg-gradient-to-b from-emerald-300 to-emerald-500 text-black font-bold text-[13.5px] disabled:opacity-50">
                {actingId === payModal.id ? <Loader2 className="w-4 h-4 animate-spin inline" /> : <CheckCircle2 className="w-4 h-4 inline mr-1" />} Confirm paid
              </button>
              <button onClick={() => setPayModal(null)} className="px-4 py-3 rounded-xl border border-white/12 hover:border-white/30 text-white/65 hover:text-white text-[13px] font-bold">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReimbursementsAdmin;
